import { useEffect, useRef, useState } from "react";
import { Check, Trash2 } from "lucide-react";
import type { RatingWithEffectiveDebits } from "../../types";
import { effectiveRatingDebits } from "../../calc";
import { getApi } from "../../api";
import { notify } from "../../store/uiStore";
import { reportError } from "../../services/eventBus";
import { trackEvent } from "../../telemetry/appInsights";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { TagPill } from "../Pills";
import { NumberInput, TextInput, ToggleInput } from "../fields";

interface RatingRowEditorProps {
  rating: RatingWithEffectiveDebits;
  /** Highlighted and scrolled into view (the row the actuary clicked in the customizations list). */
  isFocused?: boolean;
  onChanged: () => Promise<void>;
  onDelete: () => void;
  children?: never;
}

const INPUT_CELL = "px-1.5 py-1.5 align-middle";
const TEXT_CELL = "px-3 py-2.5 align-middle";

/**
 * One rating: custom rows edit text, time, and base debits inline; every row
 * takes a Multiplier or an Override (one or the other) and an Active toggle.
 * Save appears only once something changed. The Notes field is the audit note
 * for a debit modification and, on a custom rating without one, the rating's own note.
 */
export function RatingRowEditor({ rating, isFocused = false, onChanged, onDelete }: RatingRowEditorProps) {
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (isFocused) rowRef.current?.scrollIntoView({ block: "center" });
  }, [isFocused]);

  const initialNotes = rating.modification ? rating.modification.notes : (rating.notes ?? "");
  const [ratingText, setRatingText] = useState(rating.ratingText);
  const [time, setTime] = useState(rating.time ?? "");
  const [baseDebits, setBaseDebits] = useState<number | null>(rating.baseDebits);
  const [multiplier, setMultiplier] = useState<number | null>(rating.modification?.multiplier ?? null);
  const [overrideDebits, setOverrideDebits] = useState<number | null>(rating.modification?.overrideDebits ?? null);
  const [notes, setNotes] = useState(initialNotes);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const shortName = rating.ratingText.replace("Rx : ", "");

  const ratingDirty = rating.isCustom && (ratingText !== rating.ratingText || time !== (rating.time ?? "") || baseDebits !== rating.baseDebits);
  const modificationDirty = multiplier !== (rating.modification?.multiplier ?? null) || overrideDebits !== (rating.modification?.overrideDebits ?? null);
  const notesDirty = notes !== initialNotes;
  const hasModificationDraft = multiplier !== null || overrideDebits !== null;
  const isDirty = ratingDirty || modificationDirty || (notesDirty && (rating.isCustom || hasModificationDraft));
  const currentBase = baseDebits ?? rating.baseDebits;

  const liveEffective = effectiveRatingDebits(
    { id: rating.ratingId, guidelineRatingNumber: null, conditionId: rating.conditionId, ratingText, time: time || null, code: null, baseDebits: currentBase, notes: null, isMedication: rating.isMedication, isCustom: rating.isCustom, introducedReleaseName: null, isActive: true },
    hasModificationDraft ? { id: "draft", ratingId: rating.ratingId, multiplier, overrideDebits, notes, authorUserId: "", createdUtc: "", isActive: true } : null,
  );
  const isAdjusted = liveEffective !== currentBase;

  const handleMultiplier = (value: number | null) => {
    setMultiplier(value);
    if (value !== null) setOverrideDebits(null);
  };
  const handleOverride = (value: number | null) => {
    setOverrideDebits(value);
    if (value !== null) setMultiplier(null);
  };

  const handleSave = async () => {
    if (rating.isCustom && (ratingText.trim() === "" || baseDebits === null)) return;
    const savesModification = modificationDirty || (rating.modification !== null && notesDirty);
    if (savesModification && hasModificationDraft && notes.trim() === "") {
      setNotesError("Notes are required on any debit change");
      return;
    }
    setNotesError(null);
    setIsSaving(true);
    try {
      if (rating.isCustom && (ratingDirty || (notesDirty && !hasModificationDraft && rating.modification === null))) {
        await getApi().updateRating(rating.ratingId, { ratingText: ratingText.trim(), time: time.trim() || null, baseDebits: currentBase, notes: notes.trim() || null });
      }
      if (savesModification) {
        const updated = await getApi().saveDebitModification(rating.ratingId, { multiplier, overrideDebits, notes: notes.trim() || "Modification removed" });
        trackEvent("DebitModificationSaved", { ratingId: String(rating.ratingId), multiplier: String(multiplier ?? ""), override: String(overrideDebits ?? "") });
        notify(`${updated.ratingText} now rates ${updated.effectiveDebits} debits. Skynet sees the change immediately.`, "success");
      } else {
        notify(`Saved ${ratingText.trim()}.`, "success");
      }
      await onChanged();
    } catch (error) {
      reportError(error, "RatingRowEditor.save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (isActive: boolean) => {
    try {
      await getApi().updateRating(rating.ratingId, { isActive });
      notify(`${rating.ratingText} is now ${isActive ? "active" : "inactive"}.`, "success");
      await onChanged();
    } catch (error) {
      reportError(error, "RatingRowEditor.toggleActive");
    }
  };

  return (
    <tr ref={rowRef} aria-current={isFocused ? "true" : undefined} className={`border-b border-gray-100 ${rating.isActive ? "" : "opacity-60"} ${isFocused ? "bg-brand-green-05" : isDirty ? "bg-amber-50/60" : ""}`}>
      {rating.isCustom ? (
        <td className={INPUT_CELL}>
          <span className="flex items-center gap-2">
            <TextInput id={`rating-text-${rating.ratingId}`} label={`Rating text for ${shortName}`} hideLabel value={ratingText} onChange={setRatingText} size="sm" />
            {rating.isMedication && <TagPill label="Rx" />}
            <TagPill label="Custom" tone="green" />
          </span>
        </td>
      ) : (
        <td className={TEXT_CELL}>
          <span className="flex flex-wrap items-center gap-1.5 font-medium text-gray-800">
            {rating.ratingText}
            {rating.isMedication && <TagPill label="Rx" />}
          </span>
          {rating.notes && <span className="block text-xs text-gray-500">{rating.notes}</span>}
        </td>
      )}
      {rating.isCustom ? (
        <td className={INPUT_CELL}>
          <TextInput id={`rating-time-${rating.ratingId}`} label={`Time for ${shortName}`} hideLabel value={time} onChange={setTime} placeholder="Timing" size="sm" />
        </td>
      ) : (
        <td className={`${TEXT_CELL} text-gray-500`}>{rating.time ?? <span className="text-gray-300">–</span>}</td>
      )}
      {rating.isCustom ? (
        <td className={INPUT_CELL}>
          <NumberInput id={`rating-debits-${rating.ratingId}`} label={`Debits for ${shortName}`} hideLabel value={baseDebits} onChange={setBaseDebits} min={0} size="sm" />
        </td>
      ) : (
        <td className={`${TEXT_CELL} text-right tabular-nums text-gray-700`}>{rating.baseDebits}</td>
      )}
      <td className={INPUT_CELL}>
        <NumberInput id={`multiplier-${rating.ratingId}`} label={`Multiplier for ${shortName}`} hideLabel value={multiplier} onChange={handleMultiplier} readOnly={overrideDebits !== null} allowDecimal step={0.1} min={0} placeholder="×" isOverridden={multiplier !== null} size="sm" />
      </td>
      <td className={INPUT_CELL}>
        <NumberInput id={`override-${rating.ratingId}`} label={`Override for ${shortName}`} hideLabel value={overrideDebits} onChange={handleOverride} readOnly={multiplier !== null} min={0} placeholder="=" isOverridden={overrideDebits !== null} size="sm" />
      </td>
      <td className={`${TEXT_CELL} text-right tabular-nums`}>
        <span className={isAdjusted ? "rounded-md bg-brand-green-10 px-2 py-0.5 font-semibold text-brand-green-dark" : "text-gray-400"} title={isAdjusted ? `Base ${currentBase}, effective ${liveEffective}` : "Same as base"}>
          {liveEffective}
        </span>
      </td>
      <td className={INPUT_CELL}>
        <TextInput id={`notes-${rating.ratingId}`} label={`Notes for ${shortName}`} hideLabel value={notes} onChange={setNotes} error={notesError} placeholder={hasModificationDraft ? "Why (required)" : "Notes"} size="sm" />
      </td>
      <td className={`${TEXT_CELL} text-center`}>
        <ToggleInput id={`rating-active-${rating.ratingId}`} label={`Active: ${rating.ratingText}`} hideLabel checked={rating.isActive} onChange={(value) => void handleToggleActive(value)} />
      </td>
      <td className={`${INPUT_CELL} text-right`}>
        <span className="inline-flex items-center justify-end gap-1">
          <Button size="sm" icon={Check} onClick={() => void handleSave()} isLoading={isSaving} className={isDirty || isSaving ? "" : "invisible"} aria-hidden={isDirty || isSaving ? undefined : true} tabIndex={isDirty || isSaving ? undefined : -1}>
            Save
          </Button>
          {rating.isCustom ? <IconButton icon={Trash2} label={`Delete ${rating.ratingText}`} tone="danger" size="sm" onClick={onDelete} /> : <span className="inline-block w-8" aria-hidden="true" />}
        </span>
      </td>
    </tr>
  );
}
